import React, { useState } from 'react';
import DropdownMenuButton from '@components/EntitiesDropdownMenu/DropdownMenuButton';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { Dropdown, Menu, message } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import './EntitiesDropdownMenu.less';

import { CampaignItemDto } from '@api/Api';
import { apiClient } from '@api/client';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { ActionToast } from '@components/Toasts';
import {
  restoreRemovedCampaign,
  setFavoriteCampaignAction
} from '@redux/actions/campaignAction';
import {
  toggleEntityDeletionModal,
  toggleCampaignInfoModal,
  toggleCampaignInvitationModal,
  toggleRenameEntityModal,
  toggleBillingModal,
  toggleApprovalConflictAssetsModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';

type CampaignDropDownMenuProps = {
  campaign: CampaignItemDto;
  onDeleteCallback?: () => void;
  placement?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
  overlayClassName?: string;
  triggerClassName?: string;
};

export function CampaignMenu({
  campaign,
  onDeleteCallback,
  setIsDropDownOpen
}: {
  campaign: CampaignItemDto;
  onDeleteCallback?: () => void;
  setIsDropDownOpen?: (v: boolean) => void;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace();

  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;
  const hasExternalContributors =
    !!currentOrganization.entity?.features.externalContributors;

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.campaign.campaignControllerCopyCampaign({
        workspaceId: campaign.workspaceId,
        campaignId: campaign.id,
        copyAssets: true,
        copyComments: true,
        includeMembers: true
      });
      return data;
    },
    onSuccess: ({ jobId }) => {
      message.loading({
        key: `copy-progress-${jobId}`,
        className: 'message-dark-modal',
        content: `Copying media...`
      });
    },
    onError: () => {
      message.error({
        className: 'message-dark-modal',
        content: `Something went wrong`
      });
    }
  });

  const deletedItems: ItemType[] = [
    ...(campaign.permissions.delete
      ? [
          {
            key: 'delete_forever',
            label: 'Delete forever',
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: campaign,
                  page: 'campaign',
                  permanent: true,
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    ...(campaign.permissions.delete
      ? [
          {
            key: 'restore',
            label: 'Restore',
            onClick: () => {
              dispatch(restoreRemovedCampaign({ campaignId: campaign.id }));
            }
          }
        ]
      : [])
  ];

  const activeItems: ItemType[] = [
    {
      key: 'campaign_info',
      label: 'Project info',
      onClick: () => {
        dispatch(
          toggleCampaignInfoModal({
            activeTab: 'details',
            campaign,
            visible: true
          })
        );
      }
    },
    ...(campaign.permissions.changeDetails
      ? [
          {
            key: 'rename',
            label: 'Rename',
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  entity: campaign,
                  page: 'campaign',
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    ...(currentWorkspace.permissions.createCampaigns
      ? [
          {
            key: 'duplicate',
            label: duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate',
            onClick: () => {
              if (duplicateMutation.isPending) return;
              duplicateMutation.mutate();
            }
          }
        ]
      : []),
    ...(campaign.permissions.inviteContributors
      ? [
          {
            key: 'add_contributor',
            label: (
              <UpgradePlanTooltip
                text=" to a paid plan to add external contributors."
                isCanVisible={!hasExternalContributors}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={
                    !hasExternalContributors
                      ? {
                          display: 'flex',
                          alignItems: 'center'
                        }
                      : undefined
                  }
                >
                  Add сontributor{' '}
                  {!hasExternalContributors && (
                    <UpgradePlanSvg style={{ marginLeft: 4 }} />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            onClick: () => {
              if (!hasExternalContributors) {
                dispatch(toggleBillingModal({ visible: true }));
                return;
              }
              dispatch(
                toggleCampaignInvitationModal({
                  visible: true,
                  campaign
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'create_approval_routing',
      label: (
        <UpgradePlanTooltip
          text={
            !canUseBatchApprovals || !awFeatureEnabled
              ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
              : 'Click to route all media in project and folders for approval'
          }
          isCanVisible={true}
          placement="right"
          isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
          innerStyle={{
            transform: 'translate(-30px, 0)',
            maxWidth: '300px'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}
          >
            Create approval routing
            {(!canUseBatchApprovals || !awFeatureEnabled) && (
              <UpgradePlanSvg className="upgrade-icon" />
            )}
          </div>
        </UpgradePlanTooltip>
      ),
      onClick: () => {
        if (!canUseBatchApprovals || !awFeatureEnabled) {
          dispatch(toggleBillingModal({ visible: true }));
          return;
        }
        dispatch(
          toggleApprovalConflictAssetsModal({
            visible: true,
            title: 'Route project for approval',
            campaignId: campaign.id,
            workspaceId: campaign.workspaceId,
            callback: (aw) => {
              if (aw) {
                navigate(
                  `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                );
                return;
              }
              navigate(`/campaigns/single/${campaign.id}?tab=approvals`);
            }
          })
        );
      }
    },
    ...(campaign?.permissions.changeAdditionalPermissions
      ? [
          {
            key: 'contributor_permissions',
            label: (
              <UpgradePlanTooltip
                text=" to a paid plan to manage contributor permissions."
                isCanVisible={!hasExternalContributors}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={
                    !hasExternalContributors
                      ? {
                          display: 'flex',
                          alignItems: 'center'
                        }
                      : undefined
                  }
                >
                  Сontributor permissions{' '}
                  {!hasExternalContributors && (
                    <UpgradePlanSvg style={{ marginLeft: 4 }} />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            onClick: () => {
              if (!hasExternalContributors) {
                dispatch(toggleBillingModal({ visible: true }));
                return;
              }
              dispatch(
                toggleCampaignInfoModal({
                  activeTab: 'settings',
                  campaign,
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'toggle_favorite',
      label: !campaign.isFavorite ? 'Add to favorite' : 'Remove from favorite',
      onClick: () => {
        if (!campaign) return;
        dispatch(
          setFavoriteCampaignAction({
            campaignId: campaign.id || '',
            isFavorite: !campaign.isFavorite
          })
        );
        if (campaign.isFavorite) {
          toast(
            <ActionToast
              title="Project has been removed from favorites"
              onUndo={undefined}
              description={undefined}
              closeToast={undefined}
            />,
            {
              hideProgressBar: true,
              bodyClassName: 'toast_container--invitation'
            }
          );
        }
      }
    },
    {
      key: 'share_project_link',
      label: 'Share project link',
      onClick: () => {
        navigator.clipboard.writeText(
          `${window.location.origin}/campaigns/single/${campaign.id}`
        );
        message.success({
          content: 'Copied link to clipboard',
          className: 'message-dark-modal',
          icon: <SuccessSvg className="anticon" />
        });
      }
    },
    ...(campaign.permissions.delete
      ? [
          {
            key: 'delete',
            label: 'Delete',
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: campaign,
                  page: 'campaign',
                  permanent: false,
                  visible: true,
                  ...(onDeleteCallback ? { func: onDeleteCallback } : {})
                })
              );
            }
          }
        ]
      : [])
  ];

  const isDeleted = !!campaign.deletedAt;
  const items = isDeleted ? deletedItems : activeItems;

  return (
    <Menu
      className="entities-dropdown-menu-overlay"
      items={items}
      onClick={(e) => {
        e.domEvent.stopPropagation();
        if (setIsDropDownOpen) setIsDropDownOpen(false);
      }}
    />
  );
}

function CampaignDropDownMenu({
  campaign,
  onDeleteCallback,
  placement = 'bottomRight',
  overlayClassName = 'entities-dropdown-menu-overlay',
  triggerClassName
}: CampaignDropDownMenuProps) {
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const isDeleted = !!campaign.deletedAt;

  return (
    <Dropdown
      disabled={isDeleted && !campaign.permissions.delete}
      trigger={['click']}
      onOpenChange={(value) => setIsDropDownOpen(value)}
      open={isDropDownOpen}
      overlay={
        <CampaignMenu
          campaign={campaign}
          onDeleteCallback={onDeleteCallback}
          setIsDropDownOpen={setIsDropDownOpen}
        />
      }
      overlayClassName={overlayClassName}
      placement={placement}
    >
      <DropdownMenuButton
        onOpenChange={setIsDropDownOpen}
        className={triggerClassName}
      />
    </Dropdown>
  );
}

export default CampaignDropDownMenu;
